import React, { useState } from "react";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "Hello DearDr", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Thanks! We'll get back shortly.");
      setForm({ name: "", email: "", subject: "Hello DearDr", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Contact
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mt-4">
            Talk to the DearDr team
          </h2>
          <p className="text-slate-600 mt-3">
            Questions about DearDr Pro, partnerships or anything else — drop us a line and we&apos;ll respond within 24 hours.
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-50 ring-1 ring-blue-600/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Email</div>
                <div className="font-semibold text-slate-900">hello@deardr.co.in</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-50 ring-1 ring-blue-600/10 flex items-center justify-center">
                <Phone className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Support</div>
                <div className="font-semibold text-slate-900">+91 90000 00000</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-50 ring-1 ring-blue-600/10 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Location</div>
                <div className="font-semibold text-slate-900">India · Remote-first</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-6 sm:p-8" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-name">Your name</Label>
              <Input id="c-name" required value={form.name} onChange={handle("name")} placeholder="Dr. Aanya Sharma" data-testid="contact-input-name" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" required value={form.email} onChange={handle("email")} placeholder="you@example.com" data-testid="contact-input-email" className="mt-1.5" />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="c-subject">Subject</Label>
            <Input id="c-subject" value={form.subject} onChange={handle("subject")} data-testid="contact-input-subject" className="mt-1.5" />
          </div>
          <div className="mt-4">
            <Label htmlFor="c-message">Message</Label>
            <Textarea id="c-message" required value={form.message} onChange={handle("message")} placeholder="How can we help?" rows={6} data-testid="contact-input-message" className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="mt-5 h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold">
            <Send className="h-4 w-4 mr-2" />
            {loading ? "Sending…" : "Send message"}
          </Button>
        </form>
      </div>
    </section>
  );
}
